/**
 * FilterPanel — compound filter rule builder.
 *
 * Supports multiple filter groups, each with an AND/OR conjunction.
 * Groups are always ANDed together, giving full (A OR B) AND (C OR D) expressivity.
 */

import { useState } from 'react';
import { X, Plus, ChevronLeft, Check, Trash2 } from 'lucide-react';
import type { FilterRule, FilterGroup } from '../hooks/useItemListState';
import { resolveFilterFieldGroups, flattenFilterFieldGroups } from '../config/filterFields';
import { useDynamicFilterOptions } from '../hooks/useDynamicFilterOptions';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filterGroups: FilterGroup[];
  onAddRule: (rule: Omit<FilterRule, 'id'>, groupId?: string) => void;
  onRemoveRule: (groupId: string, ruleId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onSetConjunction: (groupId: string, conjunction: 'AND' | 'OR') => void;
  onAddGroup: () => string;
  onClearAll: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function FilterPanel({
  isOpen,
  onClose,
  filterGroups,
  onAddRule,
  onRemoveRule,
  onRemoveGroup,
  onSetConjunction,
  onAddGroup,
  onClearAll,
}: FilterPanelProps) {
  // Whether we're in rule-addition mode; separate from targetGroupId so null target works
  const [isAddingRule, setIsAddingRule] = useState(false);
  // Which group to add to (null = will create a new group on apply)
  const [targetGroupId, setTargetGroupId] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  // Date-range state: [start, end] as YYYY-MM-DD strings
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  // Numeric range state: min/max as strings (empty = unbounded on that side)
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');

  // Dynamic options + custom fields, built from store hooks
  const { dynamicOptionsMap, isSubjectTypesEnabled, enabledCustomFields } = useDynamicFilterOptions();

  const resolvedGroups = resolveFilterFieldGroups({
    dynamicOptionsMap,
    customFields: enabledCustomFields,
    includeMetaCategory: isSubjectTypesEnabled,
  });
  const allFieldsWithDynamic = flattenFilterFieldGroups(resolvedGroups);

  if (!isOpen) return null;

  const fieldDef = selectedField ? allFieldsWithDynamic.find((f) => f.field === selectedField) : null;
  const totalRules = filterGroups.reduce((s, g) => s + g.rules.length, 0);

  const handleStartAddRule = (groupId: string | null) => {
    setTargetGroupId(groupId);
    setSelectedField(null);
    setSelectedValues(new Set());
    setDateStart('');
    setDateEnd('');
    setRangeMin('');
    setRangeMax('');
    setIsAddingRule(true);
  };

  const handleBack = () => {
    if (selectedField) {
      setSelectedField(null);
      setSelectedValues(new Set());
      setDateStart('');
      setDateEnd('');
      setRangeMin('');
      setRangeMax('');
    } else {
      setIsAddingRule(false);
      setTargetGroupId(null);
    }
  };

  const isApplyEnabled = fieldDef
    ? fieldDef.type === 'date-range'
      ? !!(dateStart || dateEnd)
      : fieldDef.type === 'range'
        ? !!(rangeMin !== '' || rangeMax !== '')
        : selectedValues.size > 0
    : false;

  const handleApply = () => {
    if (!fieldDef || !isApplyEnabled) return;

    if (fieldDef.type === 'date-range') {
      const parts: string[] = [];
      if (dateStart) parts.push(`from ${dateStart}`);
      if (dateEnd) parts.push(`to ${dateEnd}`);
      onAddRule(
        {
          field: fieldDef.field,
          label: fieldDef.label,
          operator: 'date-range',
          value: [dateStart, dateEnd] as [string, string],
          displayValue: parts.join(' '),
        },
        targetGroupId ?? undefined,
      );
    } else if (fieldDef.type === 'range') {
      const min = rangeMin !== '' ? Number(rangeMin) : -Infinity;
      const max = rangeMax !== '' ? Number(rangeMax) : Infinity;
      const parts: string[] = [];
      if (rangeMin !== '') parts.push(`≥ ${rangeMin}`);
      if (rangeMax !== '') parts.push(`≤ ${rangeMax}`);
      onAddRule(
        {
          field: fieldDef.field,
          label: fieldDef.label,
          operator: 'range',
          value: [min, max] as [number, number],
          displayValue: parts.join(' and ') || 'Any',
        },
        targetGroupId ?? undefined,
      );
    } else if (fieldDef.type === 'text') {
      const text = Array.from(selectedValues)[0] ?? '';
      onAddRule(
        {
          field: fieldDef.field,
          label: fieldDef.label,
          operator: 'text',
          value: text,
          displayValue: text,
        },
        targetGroupId ?? undefined,
      );
    } else {
      const values = Array.from(selectedValues);
      const displayLabels = values
        .map((v) => fieldDef.options?.find((o) => o.value === v)?.label ?? v)
        .join(', ');
      onAddRule(
        {
          field: fieldDef.field,
          label: fieldDef.label,
          operator: fieldDef.type === 'boolean' ? 'boolean' : 'in',
          value: fieldDef.type === 'boolean' ? values[0] === 'true' : values,
          displayValue: fieldDef.type === 'boolean'
            ? (values[0] === 'true' ? 'Yes' : 'No')
            : displayLabels,
        },
        targetGroupId ?? undefined,
      );
    }
    setIsAddingRule(false);
    setTargetGroupId(null);
    setSelectedField(null);
    setSelectedValues(new Set());
    setDateStart('');
    setDateEnd('');
    setRangeMin('');
    setRangeMax('');
  };

  const toggleValue = (val: string) => {
    setSelectedValues((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  // On a phone the panel is a full screen over the list (X closes it), not a 288px
  // strip beside it: beside it, the rows were squeezed to a 60px column.
  return (
    <div className="w-72 max-md:fixed max-md:inset-0 max-md:z-50 max-md:w-full max-md:border-r-0 border-r border-[var(--rule)]/80 bg-[var(--bg-raised)] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
        <div className="flex items-center gap-2">
          {isAddingRule && (
            <button
              onClick={handleBack}
              className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">
            {selectedField ? fieldDef?.label : isAddingRule ? 'Choose field' : 'Filters'}
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          {totalRules > 0 && !isAddingRule && (
            <button
              onClick={onClearAll}
              className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)] font-medium px-1.5 py-0.5 rounded hover:bg-[var(--hover-tint)] transition-colors"
            >
              Clear all
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ── Field picker (step 1) ─────────────────────────── */}
        {isAddingRule && !selectedField && (
          <div className="p-4 space-y-4">
            {resolvedGroups.map((group) => {
              return (
                <div key={group.label}>
                  <div className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest mb-1.5">
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {group.fields.map((def) => (
                      <button
                        key={def.field}
                        onClick={() => setSelectedField(def.field)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--surface)] transition-colors group"
                      >
                        <div className="text-left">
                          <div className="text-[var(--text-pri)] font-medium">{def.label}</div>
                          {def.description && (
                            <div className="text-[11px] text-[var(--text-ter)]">{def.description}</div>
                          )}
                        </div>
                        <Plus className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Value picker (step 2) ─────────────────────────── */}
        {isAddingRule && selectedField && fieldDef && (
          <div className="p-4">
            {fieldDef.type === 'set' && fieldDef.options && (
              <div className="space-y-1">
                {fieldDef.options.map((opt) => {
                  const isChecked = selectedValues.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleValue(opt.value)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm  border transition-all duration-150 ${
                        isChecked
                          ? 'border-[var(--slate)] bg-[var(--slate-dim)]/60 text-[var(--text-ter)]'
                          : 'border-transparent bg-[var(--surface)]/50 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]/80'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
                        isChecked ? 'bg-[var(--slate)] border-[var(--slate)]' : 'border-[var(--rule)] bg-[var(--bg-raised)]'
                      }`}>
                        {isChecked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                      </div>
                      {opt.dot && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${opt.dot}`} />}
                      <span className="font-medium">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {fieldDef.type === 'boolean' && (
              <div className="space-y-1">
                {[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }].map((opt) => {
                  const isChecked = selectedValues.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedValues(new Set([opt.value]))}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm  border transition-all duration-150 ${
                        isChecked
                          ? 'border-[var(--slate)] bg-[var(--slate-dim)]/60 text-[var(--text-ter)]'
                          : 'border-transparent bg-[var(--surface)]/50 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]/80'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        isChecked ? 'border-[var(--slate)]' : 'border-[var(--rule)]'
                      }`}>
                        {isChecked && <div className="w-2 h-2 rounded-full bg-[var(--slate)]" />}
                      </div>
                      <span className="font-medium">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {fieldDef.type === 'date-range' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">From</label>
                  <input
                    type="date"
                    value={dateStart}
                    onChange={(e) => setDateStart(e.target.value)}
                    max={dateEnd || undefined}
                    className="w-full border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">To</label>
                  <input
                    type="date"
                    value={dateEnd}
                    onChange={(e) => setDateEnd(e.target.value)}
                    min={dateStart || undefined}
                    className="w-full border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-[11px] text-[var(--text-ter)]">Leave either end blank for an open-ended range.</p>
              </div>
            )}

            {fieldDef.type === 'range' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Minimum</label>
                  <input
                    type="number"
                    value={rangeMin}
                    onChange={(e) => setRangeMin(e.target.value)}
                    placeholder="No minimum"
                    className="w-full border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Maximum</label>
                  <input
                    type="number"
                    value={rangeMax}
                    onChange={(e) => setRangeMax(e.target.value)}
                    placeholder="No maximum"
                    className="w-full border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 focus:outline-none transition-colors"
                  />
                </div>
                <p className="text-[11px] text-[var(--text-ter)]">Leave either end blank for an open-ended range.</p>
              </div>
            )}

            {fieldDef.type === 'text' && (
              <div>
                <label className="block text-xs font-medium text-[var(--text-ter)] mb-1">Contains</label>
                <input
                  type="text"
                  value={Array.from(selectedValues)[0] ?? ''}
                  onChange={(e) => setSelectedValues(e.target.value ? new Set([e.target.value]) : new Set())}
                  placeholder="Search text…"
                  className="w-full border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-400 focus:outline-none transition-colors"
                />
              </div>
            )}

            <button
              onClick={handleApply}
              disabled={!isApplyEnabled}
              className="w-full mt-4 py-2 text-sm font-semibold bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm shadow-indigo-200"
            >
              Add rule
            </button>
          </div>
        )}

        {/* ── Group builder (default view) ─────────────────── */}
        {!isAddingRule && (
          <div className="p-4 space-y-3">
            {filterGroups.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-[var(--text-ter)] mb-4">No filters applied</p>
                <button
                  onClick={() => handleStartAddRule(null)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white text-sm font-semibold hover:bg-[var(--slate)] shadow-sm shadow-indigo-200 transition-all"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add filter
                </button>
              </div>
            ) : (
              <>
                {filterGroups.map((group, groupIndex) => (
                  <div key={group.id}>
                    {/* AND divider between groups */}
                    {groupIndex > 0 && (
                      <div className="flex items-center gap-2 my-2">
                        <div className="flex-1 h-px bg-[var(--hover-tint)]" />
                        <span className="text-[10px] font-bold text-[var(--text-ter)] uppercase tracking-widest">and</span>
                        <div className="flex-1 h-px bg-[var(--hover-tint)]" />
                      </div>
                    )}

                    {/* Group card */}
                    <div className="border border-[var(--rule)] bg-[var(--surface)]/50 overflow-hidden">
                      {/* Group header: conjunction toggle + delete */}
                      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--rule)]/70 bg-[var(--bg-raised)]">
                        <div className="flex items-center gap-1 bg-[var(--hover-tint)] p-0.5">
                          {(['AND', 'OR'] as const).map((conj) => (
                            <button
                              key={conj}
                              onClick={() => onSetConjunction(group.id, conj)}
                              className={`px-2.5 py-0.5 text-[11px] font-semibold rounded transition-all ${
                                group.conjunction === conj
                                  ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm'
                                  : 'text-[var(--text-ter)] hover:text-[var(--text-sec)]'
                              }`}
                            >
                              {conj}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-[var(--text-ter)] mr-1">
                            {group.rules.length} rule{group.rules.length !== 1 ? 's' : ''}
                          </span>
                          <button
                            onClick={() => onRemoveGroup(group.id)}
                            className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded transition-colors"
                            title="Remove group"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Rules */}
                      <div className="divide-y divide-[var(--rule)]">
                        {group.rules.map((rule, ruleIndex) => (
                          <div key={rule.id} className="flex items-start gap-2 px-3 py-2">
                            {ruleIndex > 0 && (
                              <span className="text-[9px] font-bold text-[var(--text-ter)] uppercase mt-0.5 w-6 text-right flex-shrink-0">
                                {group.conjunction}
                              </span>
                            )}
                            {ruleIndex === 0 && <div className="w-6 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-semibold text-[var(--text-sec)]">{rule.label}</span>
                              <span className="text-xs text-[var(--text-ter)] ml-1">{rule.displayValue}</span>
                            </div>
                            <button
                              onClick={() => onRemoveRule(group.id, rule.id)}
                              className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] rounded flex-shrink-0 transition-colors mt-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Add rule to this group */}
                      <div className="px-3 py-2 border-t border-[var(--rule)]">
                        <button
                          onClick={() => handleStartAddRule(group.id)}
                          className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] font-medium hover:text-[var(--text-ter)] transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add rule
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add new group */}
                <button
                  onClick={() => {
                    const id = onAddGroup();
                    handleStartAddRule(id);
                  }}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-[var(--text-ter)] border border-dashed border-[var(--rule)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)]/30 transition-all"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add filter group
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
